/**
 * Notification constants shared by the settings panel and its tests.
 *
 * The allowed reminder offsets are **not** defined here — the API sends them as
 * `allowedReminderOffsets`, and the dropdowns are built from that. A second copy
 * in the client is a copy that drifts, and the one that drifts is always the one
 * that lets someone pick an offset the server then rejects. What lives here is
 * only the presentation of those values.
 */

/** Sentinel for the "no reminder" option — `<option value>` is always a string. */
export const REMINDER_OFF = "";

/**
 * Translation key for an offset in minutes. Explicit per value rather than
 * computed, because "24 hours" and "1 hour" are different strings in every
 * language, and Romanian also wants "de" before some numbers ("24 de ore").
 */
export const reminderOffsetKey = (minutes) =>
  `Dashboard.Notifications.offsets.${minutes}`;

/**
 * The time zones offered in the picker.
 *
 * A curated list, not `Intl.supportedValuesOf("timeZone")`. Enumerating the IANA
 * database yields around 600 entries, and a 600-option dropdown is not a picker
 * — nobody scrolls it, and every one of those options is a DOM node React
 * reconciles on each render. It is also the wrong list: the businesses here are
 * European, and "Antarctica/Troll" between two of their real choices is noise.
 *
 * A zone outside this list is still perfectly storable — the API validates
 * against the full database, and the panel keeps whatever is already saved as a
 * selectable option. This governs what is *offered*, not what is *allowed*.
 */
export const getTimeZones = () => [
  "Europe/Bucharest",
  "Europe/Chisinau",
  "Europe/Athens",
  "Europe/Helsinki",
  "Europe/Kyiv",
  "Europe/Sofia",
  "Europe/Riga",
  "Europe/Tallinn",
  "Europe/Vilnius",
  "Europe/Berlin",
  "Europe/Vienna",
  "Europe/Zurich",
  "Europe/Paris",
  "Europe/Brussels",
  "Europe/Amsterdam",
  "Europe/Copenhagen",
  "Europe/Oslo",
  "Europe/Stockholm",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Malta",
  "Europe/Prague",
  "Europe/Bratislava",
  "Europe/Budapest",
  "Europe/Warsaw",
  "Europe/Ljubljana",
  "Europe/Zagreb",
  "Europe/Belgrade",
  "Europe/Sarajevo",
  "Europe/Skopje",
  "Europe/Tirane",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Lisbon",
  "Europe/Istanbul",
  "Atlantic/Reykjavik",
  "UTC",
];

/**
 * The zone's current offset, rendered as "UTC+03:00" for the picker.
 *
 * Shown next to the name because "Europe/Bucharest" is not what someone checks
 * their own setting against — the offset is. Returns an empty string rather
 * than throwing on a zone this engine cannot format.
 */
export const formatUtcOffset = (timeZone, now = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    }).formatToParts(now);

    return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
};

let cachedOptions = null;

/**
 * The picker's options as `{ id, label }`, built once per page load.
 *
 * Cached because each label costs its own `Intl.DateTimeFormat`, and building
 * them inside render meant reconstructing the whole set on every keystroke in
 * the panel.
 *
 * The offsets are a snapshot: a session open across a DST change shows the old
 * one until reload. That is a label on a picker, not a scheduling input — the
 * server converts using the zone id, never this string.
 */
export const getTimeZoneOptions = () => {
  if (cachedOptions) return cachedOptions;

  const now = new Date();
  cachedOptions = getTimeZones().map((id) => {
    const offset = formatUtcOffset(id, now);
    return { id, label: offset ? `${id} (${offset})` : id };
  });

  return cachedOptions;
};
